import React from 'react';
import { Box, Stack } from '@mui/material';

import MessageToast from '../../MessageToast';
import CloneFundOpportunityModal from '../CloneFundOpportunityModal';
import Address from '../../../models/Address';
import StyledSection from '../../styles/StyledSection';
import { StyledHeading } from '../../styles/styles';
import ActionBar from './ActionBar';
import DangerZone from './DangerZone';
import DeleteOperations from './DeleteOperations';
import FundDetailsForm from './FundDetailsForm';
import FundStatusWorkflow from './FundStatusWorkflow';
import InvestorManagement from './InvestorManagement';
import LocationManager from './LocationManager';
import StatusStepper from './StatusStepper';
import { FundOpportunityAdminProps, FundOpportunityAdminState } from './types';

export interface FundOpportunityLayoutHandlers {
  clearToast: () => void;
  showToast: (
    message: string,
    type: FundOpportunityAdminState['toastType'],
    autoHideDuration?: number,
  ) => void;
  openCloneModal: () => void;
  closeCloneModal: () => void;
  openReviewRequestModal: () => void;
  openDeleteModal: () => void;
  closeDeleteModal: () => void;
  handleDelete: (force?: boolean) => void;
  handleClone: (
    mode: import('../../../services/admin/BackendService').CloneMode,
    newName: string,
    destinationExternalId?: string,
  ) => void;
  loadData: () => void;
  handleInputChange: (
    field: keyof FundOpportunityAdminState['formData'],
    value: string | number | string[] | boolean,
    callback?: () => void,
  ) => void;
  handleSectorChange: (
    sectorOption: FundOpportunityAdminState['formData']['sector'][number],
  ) => void;
  handleSubmit: () => void;
  handleFundStatusClose: () => void;
  handleFundStatusConfirm: () => void;
  onNavigate: (path: string) => void;
  onFieldErrors: (errors: FundOpportunityAdminState['fieldErrors']) => void;
  onAddressesChange: (addresses: Address[]) => void;
  onLocationDetailsChange: (details: { country?: string; state?: string }) => void;
  onLocationLoadingChange: (loading: boolean) => void;
  onShowAddInvestorChange: (show: boolean) => void;
  onInvestorFilterChange: (filter: string) => void;
}

export interface FundOpportunityLayoutProps extends FundOpportunityAdminProps {
  state: FundOpportunityAdminState;
  handlers: FundOpportunityLayoutHandlers;
}

const FundOpportunityLayout: React.FC<FundOpportunityLayoutProps> = ({
  fundOpportunity,
  hasPermission,
  sx,
  state,
  handlers,
}) => {
  const {
    clearToast,
    showToast,
    openCloneModal,
    closeCloneModal,
    openReviewRequestModal,
    openDeleteModal,
    closeDeleteModal,
    handleDelete,
    handleClone,
    loadData,
    handleInputChange,
    handleSectorChange,
    handleSubmit,
    handleFundStatusClose,
    handleFundStatusConfirm,
    onNavigate,
    onFieldErrors,
    onAddressesChange,
    onLocationDetailsChange,
    onLocationLoadingChange,
    onShowAddInvestorChange,
    onInvestorFilterChange,
  } = handlers;

  return (
    <>
      <MessageToast
        message={state.toastMessage ?? ''}
        type={state.toastType ?? 'info'}
        open={!!state.toastMessage}
        onClose={clearToast}
        autoHideDuration={state.toastAutoHideDuration}
      />
      <Box sx={{ py: 3, ...sx }}>
        <Stack spacing={3}>
          <StyledSection tone="tone1" noTopPadding>
            <StyledHeading fullTopSpacing>{fundOpportunity.name}</StyledHeading>
            <ActionBar
              fundOpportunity={fundOpportunity}
              hasPermission={hasPermission}
              loaded={state.loaded}
              data={state.data}
              onCloneModalOpen={openCloneModal}
              onRequestReview={openReviewRequestModal}
              onNavigate={onNavigate}
              sx={{ mt: '10px', mb: '39px' }}
            />
            <StatusStepper
              fundOpportunity={fundOpportunity}
              data={state.data}
              onDataLoad={loadData}
              onShowToast={showToast}
              onFieldErrors={onFieldErrors}
            />
          </StyledSection>
          <FundDetailsForm
            formData={state.formData}
            fieldErrors={state.fieldErrors}
            isSubmitting={state.isSubmitting}
            onInputChange={handleInputChange}
            onSectorChange={handleSectorChange}
            onSubmit={handleSubmit}
          />
          <LocationManager
            fundOpportunityId={fundOpportunity.id}
            addresses={state.addresses}
            isSubmitting={state.isSubmitting}
            onAddressesChange={onAddressesChange}
            onLocationDetailsChange={onLocationDetailsChange}
            onLoadingChange={onLocationLoadingChange}
            onError={(error) => showToast(error, 'error')}
          />
          <InvestorManagement
            fundOpportunityId={fundOpportunity.id}
            loaded={state.loaded}
            data={state.data}
            hasPermission={hasPermission}
            onDataRefresh={loadData}
            highlightedInvestorId={state.highlightedInvestorId}
            showAddInvestor={!!state.addInvestor}
            onShowAddInvestorChange={onShowAddInvestorChange}
            investorFilter={state.investorFilter}
            onInvestorFilterChange={onInvestorFilterChange}
          />
          <DangerZone onDeleteClick={openDeleteModal} />
        </Stack>
      </Box>
      <DeleteOperations
        fundOpportunity={fundOpportunity}
        open={!!state.deleteModalOpen}
        submitting={!!state.deleteSubmitting}
        error={state.deleteError}
        warning={state.deleteWarning}
        onClose={closeDeleteModal}
        onDelete={handleDelete}
      />
      <FundStatusWorkflow
        fundOpportunity={fundOpportunity}
        open={state.fundStatusWorkflow.fundStatusConfirmModalOpen}
        action={state.fundStatusWorkflow.fundStatusConfirmAction}
        investor={state.fundStatusWorkflow.fundStatusConfirmInvestor}
        amount={state.fundStatusWorkflow.fundStatusConfirmAmount}
        onClose={handleFundStatusClose}
        onConfirm={handleFundStatusConfirm}
      />
      <CloneFundOpportunityModal
        open={!!state.cloneModalOpen}
        onClose={closeCloneModal}
        onSubmit={handleClone}
        isSubmitting={!!state.cloneSubmitting}
        fundOpportunity={fundOpportunity}
      />
    </>
  );
};

export default FundOpportunityLayout;
